import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import { supabase } from '../../lib/supabase';
import type { Property } from '../../lib/supabase';
import AiTenantScreeningModal from './components/AiTenantScreeningModal';
import ReportPropertyModal from './components/ReportPropertyModal';
import SeoHead from '../../components/feature/SeoHead';
import LazyImg from '../../components/base/LazyImg';
import { useAuth } from '../../hooks/useAuth';
import SimilarPropertiesSection from './components/SimilarPropertiesSection';
import { trimToDistrict } from '../../lib/locationUtils';
import { maskName } from '../../lib/locationUtils';
import { daysAgoText } from '../../lib/locationUtils';

const PLACEHOLDER_IMAGES = [
  'https://public.readdy.ai/ai/img_res/deb615f4ba548ff733505bd30a454f8a.jpg',
  'https://readdy.ai/api/search-image?query=bright%20bedroom%20interior%20with%20natural%20light%20clean%20modern%20design%20comfortable%20bed%20white%20bedding%20wooden%20furniture%20simple%20neutral%20background%20professional%20real%20estate%20photography&width=800&height=600&seq=detail_ph2&orientation=landscape',
  'https://readdy.ai/api/search-image?query=modern%20kitchen%20interior%20with%20clean%20countertops%20white%20cabinets%20stainless%20steel%20appliances%20bright%20natural%20lighting%20minimalist%20design%20professional%20real%20estate%20photo&width=800&height=600&seq=detail_ph3&orientation=landscape',
  'https://readdy.ai/api/search-image?query=clean%20modern%20bathroom%20with%20white%20tiles%20good%20lighting%20simple%20fixtures%20fresh%20and%20bright%20atmosphere%20professional%20real%20estate%20photography%20style&width=800&height=600&seq=detail_ph4&orientation=landscape',
];

const PET_LABEL: Record<string, string> = {
  yes: '✅ 允許寵物',
  no: '❌ 不允許寵物',
  discuss: '💬 可協商',
};

const WATER_LABEL: Record<string, string> = {
  taiwan_water: '💧 台灣自來水公司計費',
  included: '🆓 水費含在租金內',
  split: '📊 依人頭分攤',
  fixed: '💰 每月固定金額',
};

const ELECTRIC_LABEL: Record<string, string> = {
  taiwan_power: '⚡ 台灣電力公司計費',
  included: '🆓 電費含在租金內',
  fixed_rate: '💡 每度固定收費',
  split: '📊 依人頭分攤',
};

const ADVANTAGE_META: Record<string, { icon: string; category: string; color: string }> = {
  '近捷運站':    { icon: 'ri-train-line',              category: '交通',     color: 'text-teal-600 bg-teal-50 border-teal-200' },
  '公車站牌近':  { icon: 'ri-bus-line',                category: '交通',     color: 'text-teal-600 bg-teal-50 border-teal-200' },
  '近高鐵/台鐵': { icon: 'ri-train-wifi-line',         category: '交通',     color: 'text-teal-600 bg-teal-50 border-teal-200' },
  '附停車位':    { icon: 'ri-parking-box-line',        category: '交通',     color: 'text-teal-600 bg-teal-50 border-teal-200' },
  '近學區':      { icon: 'ri-school-line',             category: '生活機能', color: 'text-orange-600 bg-orange-50 border-orange-200' },
  '近傳統市場':  { icon: 'ri-store-2-line',            category: '生活機能', color: 'text-orange-600 bg-orange-50 border-orange-200' },
  '近超市/量販': { icon: 'ri-shopping-cart-line',      category: '生活機能', color: 'text-orange-600 bg-orange-50 border-orange-200' },
  '近醫院/診所': { icon: 'ri-hospital-line',           category: '生活機能', color: 'text-orange-600 bg-orange-50 border-orange-200' },
  '近公園綠地':  { icon: 'ri-plant-line',              category: '生活機能', color: 'text-orange-600 bg-orange-50 border-orange-200' },
  '餐廳美食聚集':{ icon: 'ri-restaurant-2-line',       category: '生活機能', color: 'text-orange-600 bg-orange-50 border-orange-200' },
  '採光良好':    { icon: 'ri-sun-line',                category: '房屋特色', color: 'text-yellow-600 bg-yellow-50 border-yellow-200' },
  '通風佳':      { icon: 'ri-windy-line',              category: '房屋特色', color: 'text-yellow-600 bg-yellow-50 border-yellow-200' },
  '環境安靜':    { icon: 'ri-volume-mute-line',        category: '房屋特色', color: 'text-yellow-600 bg-yellow-50 border-yellow-200' },
  '新裝潢':      { icon: 'ri-paint-brush-line',        category: '房屋特色', color: 'text-yellow-600 bg-yellow-50 border-yellow-200' },
  '高樓層視野佳':{ icon: 'ri-building-4-line',         category: '房屋特色', color: 'text-yellow-600 bg-yellow-50 border-yellow-200' },
  '附陽台':      { icon: 'ri-home-smile-line',         category: '房屋特色', color: 'text-yellow-600 bg-yellow-50 border-yellow-200' },
  '頂樓使用權':  { icon: 'ri-home-gear-line',          category: '房屋特色', color: 'text-yellow-600 bg-yellow-50 border-yellow-200' },
  '附儲藏室':    { icon: 'ri-archive-drawer-line',     category: '房屋特色', color: 'text-yellow-600 bg-yellow-50 border-yellow-200' },
  '社區保全':    { icon: 'ri-shield-check-line',       category: '安全管理', color: 'text-indigo-600 bg-indigo-50 border-indigo-200' },
  '監視器完善':  { icon: 'ri-camera-line',             category: '安全管理', color: 'text-indigo-600 bg-indigo-50 border-indigo-200' },
  '有電梯':      { icon: 'ri-arrow-up-down-line',      category: '安全管理', color: 'text-indigo-600 bg-indigo-50 border-indigo-200' },
  '管理員駐守':  { icon: 'ri-user-star-line',          category: '安全管理', color: 'text-indigo-600 bg-indigo-50 border-indigo-200' },
  '免仲介費':    { icon: 'ri-money-dollar-circle-line',category: '租賃優勢', color: 'text-rose-600 bg-rose-50 border-rose-200' },
  '租期彈性':    { icon: 'ri-calendar-check-line',     category: '租賃優勢', color: 'text-rose-600 bg-rose-50 border-rose-200' },
  '可短租':      { icon: 'ri-time-line',               category: '租賃優勢', color: 'text-rose-600 bg-rose-50 border-rose-200' },
  '租金可議':    { icon: 'ri-shake-hands-line',        category: '租賃優勢', color: 'text-rose-600 bg-rose-50 border-rose-200' },
  '歡迎毛孩':    { icon: 'ri-bear-smile-line',         category: '租賃優勢', color: 'text-rose-600 bg-rose-50 border-rose-200' },
  '外籍人士可':  { icon: 'ri-global-line',             category: '租賃優勢', color: 'text-rose-600 bg-rose-50 border-rose-200' },
};

// 家具 ID 對應中文名稱（相容舊資料的英文 key）
const FURNITURE_LABEL_MAP: Record<string, string> = {
  bed: '床架', wardrobe: '衣櫃', desk: '書桌椅', sofa: '沙發',
  dining: '餐桌椅', tv: '電視', fridge: '冰箱', washer: '洗衣機',
  ac: '冷氣', water_heater: '熱水器', microwave: '微波爐', stove: '瓦斯爐',
};

function parseDescription(description: string): { advantages: string[]; extraText: string } {
  const advantageMatch = description.match(/【房源優勢】([^\n]*)/);
  let advantages: string[] = [];
  let extraText = description;

  if (advantageMatch) {
    advantages = advantageMatch[1].split('、').map(s => s.trim()).filter(Boolean);
    extraText = description.replace(/【房源優勢】[^\n]*\n?/, '').trim();
  }

  return { advantages, extraText };
}

function generateNarrativeCopy(advantages: string[], extraText: string): string {
  const traffic = advantages.filter(a => ['近捷運站','公車站牌近','近高鐵/台鐵','附停車位'].includes(a));
  const life    = advantages.filter(a => ['近學區','近傳統市場','近超市/量販','近醫院/診所','近公園綠地','餐廳美食聚集'].includes(a));
  const house   = advantages.filter(a => ['採光良好','通風佳','環境安靜','新裝潢','高樓層視野佳','附陽台','頂樓使用權','附儲藏室'].includes(a));
  const safety  = advantages.filter(a => ['社區保全','監視器完善','有電梯','管理員駐守'].includes(a));
  const rental  = advantages.filter(a => ['免仲介費','租期彈性','可短租','租金可議','歡迎毛孩','外籍人士可'].includes(a));

  const parts: string[] = [];

  if (traffic.length > 0) {
    const items = traffic.join('、');
    parts.push(`交通方面，${items}，出門通勤相當方便，不論是上班族還是學生都很適合。`);
  }
  if (life.length > 0) {
    const items = life.join('、');
    parts.push(`生活機能也很完善，附近有${items}，日常採買、飲食、就醫都不成問題，生活起來十分便利。`);
  }
  if (house.length > 0) {
    const feats: string[] = [];
    if (house.includes('採光良好')) feats.push('採光充足、室內明亮');
    if (house.includes('通風佳'))   feats.push('通風良好、空氣流通');
    if (house.includes('環境安靜')) feats.push('周邊環境安靜');
    if (house.includes('新裝潢'))   feats.push('全新裝潢、質感細緻');
    if (house.includes('高樓層視野佳')) feats.push('高樓層視野開闊');
    if (house.includes('附陽台'))   feats.push('附有陽台可晾衣休憩');
    if (house.includes('頂樓使用權')) feats.push('享有頂樓使用權');
    if (house.includes('附儲藏室')) feats.push('附儲藏室收納空間充裕');
    parts.push(`房屋本身${feats.join('，')}，住起來舒適又有質感。`);
  }
  if (safety.length > 0) {
    const items = safety.join('、');
    parts.push(`安全管理方面，社區設有${items}，讓您住得安心、無後顧之憂。`);
  }
  if (rental.length > 0) {
    const rentalParts: string[] = [];
    if (rental.includes('免仲介費'))   rentalParts.push('完全零仲介費、直接與屋主聯繫');
    if (rental.includes('租期彈性'))   rentalParts.push('租期彈性好談');
    if (rental.includes('可短租'))     rentalParts.push('接受短租');
    if (rental.includes('租金可議'))   rentalParts.push('租金有議價空間');
    if (rental.includes('歡迎毛孩'))   rentalParts.push('歡迎毛孩入住');
    if (rental.includes('外籍人士可')) rentalParts.push('外籍人士亦可承租');
    if (rentalParts.length > 0) {
      parts.push(`租賃條件上，${rentalParts.join('、')}，誠心歡迎有緣的好房客。`);
    }
  }
  if (extraText) {
    parts.push(`\n屋主補充：「${extraText}」`);
  }

  return parts.join('\n\n');
}

export default function PropertyDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [property, setProperty] = useState<Property | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [activeImg, setActiveImg] = useState(0);
  const [liked, setLiked] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxImg, setLightboxImg] = useState(0);
  const [showScreeningModal, setShowScreeningModal] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);
  const [viewCount, setViewCount] = useState(0);
  const [screeningCount, setScreeningCount] = useState(0);

  useEffect(() => {
    if (id) fetchProperty();
  }, [id]);

  const fetchProperty = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('properties')
      .select('*')
      .eq('id', id)
      .maybeSingle();

    if (error || !data) {
      setNotFound(true);
    } else {
      setProperty(data);
      const newViewCount = (data.view_count || 0) + 1;
      setViewCount(newViewCount);
      setScreeningCount(data.screening_request_count || 0);
      await supabase
        .from('properties')
        .update({ view_count: newViewCount })
        .eq('id', id);
    }
    setLoading(false);
  };

  const handleOpenScreeningModal = async () => {
    if (!user && !authLoading) {
      setShowLoginPrompt(true);
      return;
    }
    setShowScreeningModal(true);
    const newCount = screeningCount + 1;
    setScreeningCount(newCount);
    await supabase
      .from('properties')
      .update({ screening_request_count: newCount })
      .eq('id', id);
  };

  const openLightbox = (idx: number) => {
    setLightboxImg(idx);
    setLightboxOpen(true);
  };

  const closeLightbox = () => setLightboxOpen(false);
  const prevImg = () => setLightboxImg((prev) => (prev - 1 + images.length) % images.length);
  const nextImg = () => setLightboxImg((prev) => (prev + 1) % images.length);

  if (loading) {
    return (
      <div className="min-h-screen bg-stone-50 flex flex-col">
        <Navbar />
        <div className="flex-1 flex flex-col items-center justify-center gap-4 pt-32">
          <i className="ri-loader-4-line text-5xl text-teal-400 animate-spin"></i>
          <p className="text-gray-500">載入房源資訊中...</p>
        </div>
        <Footer />
      </div>
    );
  }

  if (notFound || !property) {
    return (
      <div className="min-h-screen bg-stone-50 flex flex-col">
        <Navbar />
        <div className="flex-1 flex flex-col items-center justify-center gap-6 pt-32">
          <i className="ri-home-search-line text-7xl text-gray-300"></i>
          <p className="text-2xl text-gray-500">找不到此房源</p>
          <Link to="/regions" className="px-8 py-3 bg-teal-500 text-white rounded-full font-medium hover:bg-teal-600 transition-colors whitespace-nowrap cursor-pointer">
            返回房源列表
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const images: string[] = property.photos && property.photos.length > 0
    ? property.photos
    : PLACEHOLDER_IMAGES;

  const positions: string[] = property.photo_positions && property.photo_positions.length > 0
    ? property.photo_positions
    : images.map(() => '50% 50%');

  const zooms: number[] = property.photo_zooms && property.photo_zooms.length > 0
    ? property.photo_zooms
    : images.map(() => 1);

  const getPos = (idx: number) => positions[idx] ?? '50% 50%';
  const getZoom = (idx: number) => zooms[idx] ?? 1;

  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://ailanshe.com';
  const propertyJsonLd = {
    "@context": "https://schema.org",
    "@type": "RealEstateListing",
    "name": property.title,
    "description": property.description || `${property.location}，${property.area}坪，${property.rooms}房${property.bathrooms}衛，月租NT$${property.price.toLocaleString()}，本平台零仲介費，正常流程不需透過第三方代收款項`,
    "url": `${siteUrl}/property/${property.id}`,
    "image": images[0],
    "offers": {
      "@type": "Offer",
      "price": property.price,
      "priceCurrency": "TWD",
    },
    "address": {
      "@type": "PostalAddress",
      "addressLocality": property.location,
      "addressCountry": "TW"
    },
    "numberOfRooms": property.rooms,
    "landlord": {
      "@type": "Person",
      "name": property.landlord_name
    },
  };

  const features = [
    { icon: 'ri-home-4-line', label: '坪數', value: `${property.area} 坪` },
    { icon: 'ri-door-open-line', label: '房間數', value: `${property.rooms} 房` },
    { icon: 'ri-drop-line', label: '衛浴數', value: `${property.bathrooms} 衛` },
    { icon: 'ri-map-pin-line', label: '地區', value: trimToDistrict(property.location) },
  ];

  const furnitureTags = property.furniture
    ? property.furniture.split('、').filter(f => f && f !== '無家具').map(f => FURNITURE_LABEL_MAP[f] || f)
    : [];

  return (
    <div className="min-h-screen bg-stone-50">
      <SeoHead
        title={`${property.title}｜${property.location} ${property.rooms}房${property.bathrooms}衛 NT$${property.price.toLocaleString()}/月 - 藍舍AI地產`}
        description={`${trimToDistrict(property.location)}屋主直租，${property.area}坪${property.rooms}房${property.bathrooms}衛，月租NT$${property.price.toLocaleString()}，零仲介費直接聯繫屋主，${property.description ? property.description.slice(0, 60) : '真實房源安心租住'}。`}
        keywords={`${property.location},${property.rooms}房,屋主直租,零仲介費,${property.title}`}
        canonical={`/property/${property.id}`}
        ogImage={images[0]}
        jsonLd={propertyJsonLd}
      />
      <Navbar />

      {/* Login Prompt Modal */}
      {showLoginPrompt && (
        <div
          className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 backdrop-blur-sm"
          onClick={() => setShowLoginPrompt(false)}
        >
          <div
            className="bg-white w-full sm:max-w-sm sm:rounded-2xl rounded-t-2xl shadow-2xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-6 pt-6 pb-2 text-center">
              <div className="w-14 h-14 flex items-center justify-center rounded-full bg-teal-50 mx-auto mb-4">
                <i className="ri-home-smile-2-line text-teal-500 text-2xl"></i>
              </div>
              <h3 className="text-stone-800 font-bold text-base mb-2">請先登入才能預約看房</h3>
              <p className="text-stone-500 text-sm leading-relaxed">
                登入後即可使用 AI 助理預約看房、與房東直接聯繫，完全免費！
              </p>
            </div>
            <div className="px-6 pb-6 mt-5 space-y-2.5">
              <button
                onClick={() => {
                  setShowLoginPrompt(false);
                  navigate(`/login?redirect=/property/${id}`);
                }}
                className="w-full py-3 bg-teal-500 text-white text-sm font-medium rounded-xl hover:bg-teal-600 transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
              >
                <i className="ri-login-circle-line"></i>
                立即登入
              </button>
              <button
                onClick={() => {
                  setShowLoginPrompt(false);
                  navigate('/register');
                }}
                className="w-full py-3 bg-stone-50 text-stone-600 text-sm font-medium rounded-xl border border-stone-200 hover:bg-stone-100 transition-colors cursor-pointer whitespace-nowrap"
              >
                免費註冊帳號
              </button>
              <button
                onClick={() => setShowLoginPrompt(false)}
                className="w-full py-2 text-stone-400 text-xs hover:text-stone-500 transition-colors cursor-pointer"
              >
                稍後再說
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Lightbox */}
      {lightboxOpen && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/90 backdrop-blur-sm"
          onClick={closeLightbox}
        >
          <button
            onClick={closeLightbox}
            className="absolute top-5 right-5 w-11 h-11 flex items-center justify-center bg-white/10 hover:bg-white/20 text-white rounded-full transition-colors cursor-pointer z-10"
          >
            <i className="ri-close-line text-2xl"></i>
          </button>
          <div className="absolute top-5 left-1/2 -translate-x-1/2 text-white/70 text-sm font-medium">
            {lightboxImg + 1} / {images.length}
          </div>
          {images.length > 1 && (
            <button
              onClick={(e) => { e.stopPropagation(); prevImg(); }}
              className="absolute left-4 md:left-8 w-11 h-11 flex items-center justify-center bg-white/10 hover:bg-white/25 text-white rounded-full transition-colors cursor-pointer z-10"
            >
              <i className="ri-arrow-left-s-line text-2xl"></i>
            </button>
          )}
          <div
            className="relative max-w-5xl w-full mx-16 md:mx-24 flex items-center justify-center"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={images[lightboxImg]}
              alt={property.title}
              className="max-w-full max-h-full object-contain"
            />
          </div>
          {images.length > 1 && (
            <button
              onClick={(e) => { e.stopPropagation(); nextImg(); }}
              className="absolute right-4 md:right-8 w-11 h-11 flex items-center justify-center bg-white/10 hover:bg-white/25 text-white rounded-full transition-colors cursor-pointer z-10"
            >
              <i className="ri-arrow-right-s-line text-2xl"></i>
            </button>
          )}
          <div
            className="absolute bottom-5 left-1/2 -translate-x-1/2 flex gap-2"
            onClick={(e) => e.stopPropagation()}
          >
            {images.map((img, idx) => (
              <button
                key={idx}
                onClick={() => setLightboxImg(idx)}
                className={`w-12 h-9 rounded-lg overflow-hidden border-2 transition-all cursor-pointer flex-shrink-0 ${lightboxImg === idx ? 'border-white scale-110' : 'border-transparent opacity-70 hover:opacity-100'}`}
              >
                <img src={img} alt={`縮圖 ${idx + 1}`} className="w-full h-full object-cover" style={{ objectPosition: getPos(idx), transform: `scale(${getZoom(idx)})`, transformOrigin: getPos(idx) }} />
              </button>
            ))}
          </div>
        </div>
      )}

      {/* AI 看房篩選對話框 */}
      {showScreeningModal && property && (
        <AiTenantScreeningModal
          property={property}
          onClose={() => setShowScreeningModal(false)}
        />
      )}

      {/* 舉報彈窗 */}
      {showReportModal && property && (
        <ReportPropertyModal
          propertyId={property.id}
          propertyTitle={property.title}
          landlordName={property.landlord_name}
          reporterUserId={user?.id}
          reporterEmail={user?.email}
          onClose={() => setShowReportModal(false)}
        />
      )}

      {/* 手機版底部固定聯絡欄 */}
      <div
        className="md:hidden fixed bottom-0 left-0 right-0 z-40 bg-white border-t border-stone-200"
        style={{ boxShadow: '0 -4px 24px rgba(0,0,0,0.09)', paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
      >
        {/* 主操作列 */}
        <div className="flex items-center gap-3 px-4 pt-3 pb-2">
          <div className="flex-1 min-w-0">
            <div className="text-[10px] text-gray-400 leading-none mb-0.5 uppercase tracking-wide">月租金</div>
            <div className="text-2xl font-extrabold text-red-500 leading-tight">
              NT$ {property.price.toLocaleString()}
            </div>
          </div>
          <button
            onClick={() => setShowReportModal(true)}
            className="w-11 h-11 flex items-center justify-center bg-stone-100 text-stone-400 rounded-full hover:bg-rose-50 hover:text-rose-400 transition-colors cursor-pointer flex-shrink-0"
          >
            <i className="ri-flag-2-line text-lg"></i>
          </button>
          <button
            onClick={handleOpenScreeningModal}
            className="flex items-center gap-2 px-5 py-3 bg-teal-600 text-white rounded-full font-semibold text-sm whitespace-nowrap cursor-pointer active:scale-95 transition-transform flex-shrink-0"
          >
            <i className="ri-home-smile-2-line text-base"></i>
            我要看房
          </button>
        </div>
        {/* 資訊列 */}
        <div className="flex items-center justify-between px-4 pb-2.5 gap-3">
          <div className="flex items-center gap-1 text-[11px] text-gray-400">
            <i className="ri-eye-line text-xs"></i>
            <span>{viewCount} 人瀏覽</span>
          </div>
          {screeningCount > 0 ? (
            <div className="flex items-center gap-1 text-[11px] text-teal-600 font-medium">
              <i className="ri-group-line text-xs"></i>
              <span>已有 <strong>{screeningCount}</strong> 人預約</span>
            </div>
          ) : (
            <div className="flex items-center gap-1 text-[11px] text-teal-500">
              <i className="ri-group-line text-xs"></i>
              <span>搶先預約看房</span>
            </div>
          )}
          <div className="flex items-center gap-1 text-[11px] text-gray-400">
            <i className="ri-robot-2-line text-xs"></i>
            <span>AI 助理協助</span>
          </div>
        </div>
      </div>

      <div className="pt-20 md:pt-28 pb-36 md:pb-20">
        <div className="max-w-7xl mx-auto px-0 md:px-6">

          {/* 麵包屑 */}
          <div className="flex items-center gap-2 text-sm text-gray-500 mb-4 md:mb-5 flex-wrap px-4 md:px-0">
            <Link to="/" className="hover:text-teal-600 transition-colors cursor-pointer">首頁</Link>
            <i className="ri-arrow-right-s-line"></i>
            <Link to="/regions" className="hover:text-teal-600 transition-colors cursor-pointer">房源列表</Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-gray-900 font-medium line-clamp-1">{property.title}</span>
          </div>

          {/* 安全提醒橫幅 */}
          <div className="mx-4 md:mx-0 mb-5 md:mb-7 rounded-2xl border border-amber-200 bg-amber-50 overflow-hidden">
            <div className="flex items-start gap-3 px-4 py-3.5 md:px-5 md:py-4">
              <div className="w-8 h-8 flex items-center justify-center flex-shrink-0 bg-amber-100 rounded-full mt-0.5">
                <i className="ri-shield-flash-line text-amber-600 text-lg"></i>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-bold text-amber-800 mb-1 flex items-center gap-1.5">
                  <i className="ri-error-warning-fill text-amber-500 text-base"></i>
                  租屋安全提醒 — 請謹防詐騙
                </p>
                <ul className="space-y-1">
                  {[
                    '切勿在未看房前匯款或繳交任何費用（含訂金、押金）',
                    '本平台屋主直租，正常流程不需透過第三方代收款項',
                    '若對方要求使用不明連結付款或轉帳，請立即停止並向官方檢舉',
                    '簽約前請確認屋主身份與房屋產權，建議攜帶親友同行看房',
                  ].map((tip, i) => (
                    <li key={i} className="flex items-start gap-1.5 text-xs text-amber-700 leading-relaxed">
                      <i className="ri-checkbox-circle-line text-amber-500 text-sm flex-shrink-0 mt-0.5"></i>
                      {tip}
                    </li>
                  ))}
                </ul>
              </div>
              <button
                onClick={() => setShowReportModal(true)}
                className="hidden md:flex flex-shrink-0 items-center gap-1.5 px-3.5 py-2 bg-amber-500 hover:bg-amber-600 text-white text-xs font-medium rounded-full transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-flag-2-line text-sm"></i>
                舉報此房源
              </button>
            </div>
            <div className="md:hidden border-t border-amber-200 px-4 py-2.5 flex items-center justify-between bg-amber-100/50">
              <span className="text-xs text-amber-600 flex items-center gap-1">
                <i className="ri-customer-service-2-line"></i>
                發現可疑行為？
              </span>
              <button
                onClick={() => setShowReportModal(true)}
                className="flex items-center gap-1 px-3 py-1.5 bg-amber-500 hover:bg-amber-600 text-white text-xs font-medium rounded-full transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-flag-2-line"></i>
                立即舉報
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-0 md:gap-10">
            {/* 左側主內容 */}
            <div className="md:col-span-2 space-y-0 md:space-y-8">

              {/* 圖片區 */}
              <div className="bg-white md:rounded-3xl overflow-hidden md:shadow-sm">
                <div className="relative w-full overflow-hidden bg-stone-100" style={{ aspectRatio: '16/10' }}>
                  <img
                    src={images[activeImg]}
                    alt={property.title}
                    onClick={() => openLightbox(activeImg)}
                    className="w-full h-full object-cover transition-all duration-500 cursor-zoom-in"
                    style={{ objectPosition: getPos(activeImg), transform: `scale(${getZoom(activeImg)})`, transformOrigin: getPos(activeImg) }}
                  />
                  {images.length > 1 && (
                    <>
                      <button
                        onClick={(e) => { e.stopPropagation(); setActiveImg((activeImg - 1 + images.length) % images.length); }}
                        className="md:hidden absolute left-3 top-1/2 -translate-y-1/2 w-9 h-9 flex items-center justify-center bg-black/40 text-white rounded-full cursor-pointer active:scale-95 transition-transform"
                      >
                        <i className="ri-arrow-left-s-line text-xl"></i>
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); setActiveImg((activeImg + 1) % images.length); }}
                        className="md:hidden absolute right-3 top-1/2 -translate-y-1/2 w-9 h-9 flex items-center justify-center bg-black/40 text-white rounded-full cursor-pointer active:scale-95 transition-transform"
                      >
                        <i className="ri-arrow-right-s-line text-xl"></i>
                      </button>
                    </>
                  )}
                  <span className="absolute top-3 left-3 md:top-5 md:left-5 px-3 py-1 md:px-4 md:py-1.5 bg-teal-500 text-white text-xs font-medium rounded-full whitespace-nowrap">
                    零仲介費
                  </span>
                  <div className="md:hidden absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
                    {images.map((_, idx) => (
                      <span
                        key={idx}
                        className={`block rounded-full transition-all ${activeImg === idx ? 'w-4 h-1.5 bg-white' : 'w-1.5 h-1.5 bg-white/60'}`}
                      />
                    ))}
                  </div>
                  <div
                    onClick={() => openLightbox(activeImg)}
                    className="hidden md:flex absolute bottom-4 right-14 items-center gap-1.5 bg-black/50 text-white text-xs px-3 py-1.5 rounded-full backdrop-blur-sm cursor-zoom-in hover:bg-black/70 transition-colors"
                  >
                    <i className="ri-zoom-in-line text-sm"></i>
                    點擊放大
                  </div>
                  <button
                    onClick={() => setLiked(!liked)}
                    className={`absolute top-3 right-3 md:top-5 md:right-5 w-9 h-9 md:w-10 md:h-10 flex items-center justify-center rounded-full shadow-md transition-all cursor-pointer ${liked ? 'bg-red-500 text-white' : 'bg-white text-gray-600 hover:bg-red-50'}`}
                  >
                    <i className={liked ? 'ri-heart-fill' : 'ri-heart-line'}></i>
                  </button>
                </div>

                {/* 縮圖條（桌機） */}
                <div className="hidden md:flex gap-3 p-4 overflow-x-auto">
                  {images.map((img, idx) => (
                    <button
                      key={idx}
                      onClick={() => setActiveImg(idx)}
                      onDoubleClick={() => openLightbox(idx)}
                      className={`w-20 h-16 rounded-xl overflow-hidden flex-shrink-0 border-2 transition-all cursor-pointer ${activeImg === idx ? 'border-teal-500 scale-105' : 'border-transparent opacity-70 hover:opacity-100'}`}
                    >
                      <LazyImg
                        src={img}
                        alt={`圖片${idx + 1}`}
                        containerClassName="w-full h-full"
                        imgClassName="w-full h-full object-cover object-top"
                      />
                    </button>
                  ))}
                </div>
              </div>

              {/* 基本資訊 */}
              <div className="bg-white md:rounded-3xl px-4 py-5 md:p-8 md:shadow-sm border-b border-stone-100 md:border-0">
                <div className="flex items-start justify-between gap-3 mb-4">
                  <div className="flex-1 min-w-0">
                    <h1 className="text-lg md:text-2xl font-bold text-gray-900 mb-1.5 leading-snug">{property.title}</h1>
                    <p className="text-gray-500 flex items-center gap-1 text-xs md:text-sm">
                      <i className="ri-map-pin-line text-teal-500"></i>
                      {trimToDistrict(property.location)}
                    </p>
                    <div className="flex items-center gap-3 mt-1.5 flex-wrap">
                      <span className="text-xs text-gray-400 flex items-center gap-1">
                        <i className="ri-eye-line text-xs"></i>
                        {viewCount} 人瀏覽
                      </span>
                      {screeningCount > 0 && (
                        <span className="text-xs text-teal-600 flex items-center gap-1 font-medium">
                          <i className="ri-group-line text-teal-500 text-xs"></i>
                          {screeningCount} 人預約看房
                        </span>
                      )}
                      {property.created_at && (
                        <span className="text-xs text-stone-400 flex items-center gap-1">
                          <i className="ri-time-line text-xs"></i>
                          {daysAgoText(property.created_at)}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="hidden md:block text-right shrink-0">
                    <div className="text-3xl font-bold text-red-500">NT$ {property.price.toLocaleString()}</div>
                    <div className="text-sm text-gray-400">/ 月</div>
                  </div>
                </div>

                <div className="flex items-start gap-2.5 bg-teal-50 border border-teal-100 rounded-2xl px-3 py-2.5 md:px-4 md:py-3 mb-4">
                  <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <i className="ri-shield-check-fill text-teal-600 text-base"></i>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-bold text-teal-700 mb-0.5">本平台謝絕仲介</p>
                    <p className="text-xs text-teal-600 leading-relaxed">
                      所有標示價格均不含仲介費用，零仲介費零手續費。
                    </p>
                  </div>
                  <span className="ml-auto flex-shrink-0 px-2 py-0.5 bg-teal-500 text-white text-xs font-bold rounded-full whitespace-nowrap">
                    零仲介費
                  </span>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-4">
                  {features.map((f) => (
                    <div key={f.label} className="bg-stone-50 rounded-xl md:rounded-2xl p-3 md:p-4 text-center">
                      <div className="w-8 h-8 md:w-10 md:h-10 flex items-center justify-center mx-auto mb-1.5 md:mb-2 bg-teal-50 rounded-full">
                        <i className={`${f.icon} text-teal-600 text-sm md:text-lg`}></i>
                      </div>
                      <div className="text-[10px] md:text-xs text-gray-500 mb-0.5">{f.label}</div>
                      <div className="text-sm md:text-sm font-bold text-gray-900 leading-tight">{f.value}</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* 房源描述 */}
              {property.description && (() => {
                const { advantages, extraText } = parseDescription(property.description);
                const narrative = generateNarrativeCopy(advantages, extraText);
                const displayText = narrative || property.description;

                return (
                  <div className="bg-white md:rounded-3xl px-4 py-5 md:p-8 md:shadow-sm border-b border-stone-100 md:border-0">
                    <h2 className="text-base md:text-lg font-bold text-gray-900 flex items-center gap-2 mb-4 md:mb-5">
                      <i className="ri-quill-pen-line text-teal-500"></i>
                      房源介紹
                    </h2>
                    <div className="space-y-4">
                      {displayText.split('\n\n').map((para, idx) => {
                        const isOwnerNote = para.startsWith('屋主補充：');
                        if (isOwnerNote) {
                          return (
                            <div key={idx} className="flex gap-3 bg-stone-50 rounded-2xl p-4 border border-stone-200 mt-2">
                              <div className="w-8 h-8 flex items-center justify-center flex-shrink-0 bg-teal-100 rounded-full mt-0.5">
                                <i className="ri-user-voice-line text-teal-600 text-sm"></i>
                              </div>
                              <div>
                                <p className="text-xs font-bold text-gray-400 mb-1 uppercase tracking-wide">屋主親筆補充</p>
                                <p className="text-gray-700 text-sm leading-relaxed whitespace-pre-line">
                                  {para.replace('屋主補充：', '').replace(/^「|」$/g, '')}
                                </p>
                              </div>
                            </div>
                          );
                        }
                        return (
                          <p key={idx} className="text-gray-700 text-sm leading-[1.9] tracking-wide">
                            {para}
                          </p>
                        );
                      })}
                    </div>

                    {advantages.length > 0 && (
                      <div className="mt-5 pt-4 border-t border-stone-100">
                        <p className="text-xs text-gray-400 mb-3 font-medium">房源亮點速覽</p>
                        <div className="flex flex-wrap gap-2">
                          {advantages.map(adv => {
                            const advMeta = ADVANTAGE_META[adv];
                            return (
                              <span
                                key={adv}
                                className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium border ${advMeta?.color || 'text-gray-600 bg-gray-50 border-gray-200'}`}
                              >
                                <i className={`${advMeta?.icon || 'ri-checkbox-circle-line'} text-xs`}></i>
                                {adv}
                              </span>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })()}

              {/* 家具設備 */}
              {furnitureTags.length > 0 && (
                <div className="bg-white md:rounded-3xl px-4 py-5 md:p-8 md:shadow-sm border-b border-stone-100 md:border-0">
                  <h2 className="text-base md:text-lg font-bold text-gray-900 mb-4 md:mb-5 flex items-center gap-2">
                    <i className="ri-tools-line text-teal-500"></i>
                    設備與家具
                  </h2>
                  <div className="flex flex-wrap gap-2">
                    {furnitureTags.map((tag) => (
                      <span key={tag} className="px-3 py-1.5 bg-teal-50 text-teal-700 text-sm rounded-full font-medium border border-teal-100">
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* 費用說明 */}
              <div className="bg-white md:rounded-3xl px-4 py-5 md:p-8 md:shadow-sm border-b border-stone-100 md:border-0">
                <h2 className="text-base md:text-lg font-bold text-gray-900 mb-4 md:mb-5 flex items-center gap-2">
                  <i className="ri-money-dollar-circle-line text-teal-500"></i>
                  費用說明
                </h2>
                <div className="grid grid-cols-2 gap-2.5 md:gap-4">
                  {[
                    { label: '水費', value: WATER_LABEL[property.water_bill || ''] || '請洽屋主' },
                    { label: '電費', value: ELECTRIC_LABEL[property.electric_bill || ''] || '請洽屋主' },
                    { label: '寵物', value: PET_LABEL[property.pet_allowed || ''] || '請洽屋主' },
                    { label: '押金', value: '兩個月租金' },
                  ].map((item) => (
                    <div key={item.label} className="flex items-center justify-between p-3 md:p-4 bg-stone-50 rounded-xl">
                      <span className="text-xs md:text-sm text-gray-500 shrink-0">{item.label}</span>
                      <span className="text-xs md:text-sm font-medium text-gray-900 text-right ml-2 leading-snug">{item.value}</span>
                    </div>
                  ))}
                </div>
              </div>

            </div>

            {/* 右側聯絡欄（桌機顯示） */}
            <div className="hidden md:block space-y-4 md:space-y-6 px-4 md:px-0">
              <div className="bg-white rounded-3xl p-5 md:p-6 shadow-sm md:sticky md:top-28">
                <div className="flex items-center gap-3 md:gap-4 mb-5 md:mb-6">
                  <div className="w-12 h-12 md:w-14 md:h-14 rounded-full overflow-hidden bg-teal-100 flex items-center justify-center flex-shrink-0">
                    <i className="ri-user-line text-xl md:text-2xl text-teal-600"></i>
                  </div>
                  <div>
                    <div className="font-bold text-gray-900">{maskName(property.landlord_name)}</div>
                    <div className="text-xs text-teal-600 flex items-center gap-1 mt-0.5">
                      <i className="ri-shield-check-line"></i>
                      零仲介費
                    </div>
                  </div>
                </div>

                {/* 上架天數 */}
                {property.created_at && (
                  <div className="mb-4 flex items-center gap-2 text-xs text-stone-400">
                    <i className="ri-time-line text-stone-300"></i>
                    <span>{daysAgoText(property.created_at)}</span>
                  </div>
                )}

                <div className="flex items-center gap-3 text-sm text-gray-600">
                  <i className="ri-notification-3-line text-teal-500"></i>
                  屋主收到申請後將主動聯繫您
                </div>
                <div className="flex items-center gap-3 text-sm text-gray-600">
                  <i className="ri-calendar-check-line text-teal-500"></i>
                  可預約看房
                </div>
                <div className="flex items-center gap-3 text-sm text-gray-600">
                  <i className="ri-money-dollar-circle-line text-teal-500"></i>
                  零仲介費
                </div>

                {/* 熱度指標 */}
                <div className="flex items-center justify-between mb-4 px-3 py-2.5 bg-stone-50 rounded-2xl">
                  <span className="flex items-center gap-1.5 text-xs text-gray-500">
                    <i className="ri-eye-line text-gray-400"></i>
                    {viewCount} 人瀏覽
                  </span>
                  {screeningCount > 0 && (
                    <span className="flex items-center gap-1.5 text-xs text-teal-600 font-medium">
                      <i className="ri-group-line text-teal-500"></i>
                      {screeningCount} 人已預約
                    </span>
                  )}
                </div>

                {/* Beta 提示 */}
                <div className="mb-4 flex items-start gap-2 bg-stone-50 border border-stone-200 rounded-xl px-3 py-2.5">
                  <i className="ri-information-line text-stone-400 text-sm flex-shrink-0 mt-0.5"></i>
                  <p className="text-xs text-stone-500 leading-relaxed">
                    本平台目前為測試（Beta）階段，不對房源資訊作出保證，看房前請自行查核，謹慎決策。
                  </p>
                </div>

                <button
                  onClick={handleOpenScreeningModal}
                  className="w-full py-3.5 bg-teal-600 text-white rounded-full font-medium hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer flex items-center justify-center gap-2 text-sm"
                >
                  <i className="ri-home-smile-2-line text-base"></i>
                  我要看房
                </button>

                {/* 預約看房人數 */}
                <div className="mt-3 flex items-center justify-center gap-2 py-2.5 px-4 bg-teal-50 rounded-2xl border border-teal-100">
                  <div className="w-6 h-6 flex items-center justify-center bg-teal-100 rounded-full flex-shrink-0">
                    <i className="ri-group-line text-teal-600 text-xs"></i>
                  </div>
                  {screeningCount > 0 ? (
                    <span className="text-xs text-teal-700 font-medium">
                      已有 <span className="text-base font-bold text-teal-600">{screeningCount}</span> 人點擊預約看房
                    </span>
                  ) : (
                    <span className="text-xs text-teal-600">成為第一個預約看房的人！</span>
                  )}
                </div>

                <p className="text-center text-xs text-gray-400 flex items-center justify-center gap-1 mt-2">
                  <i className="ri-robot-2-line"></i>AI 助理將引導您完成看房申請
                </p>

                {/* 舉報按鈕 */}
                <button
                  onClick={() => setShowReportModal(true)}
                  className="mt-3 w-full flex items-center justify-center gap-1.5 py-2 text-xs text-stone-400 hover:text-rose-400 transition-colors cursor-pointer"
                >
                  <i className="ri-flag-2-line text-sm"></i>
                  舉報此房源
                </button>
              </div>

              <button
                onClick={() => navigate(-1)}
                className="w-full py-3 border border-gray-300 text-gray-600 rounded-full text-sm hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-arrow-left-line mr-2"></i>返回上一頁
              </button>
            </div>
          </div>

          {/* 相似房源推薦 */}
          {property && (
            <SimilarPropertiesSection currentProperty={property} />
          )}

        </div>
      </div>

      <Footer />
    </div>
  );
}
